import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getCurrentUserLocation } from '../utils/userLocation';
import { matchesLocation } from '../utils/locations';
import { Advertisement } from '../types/advertising';
import { getUserActivity, shouldShowAd, trackAdImpression, trackAdClick } from '../utils/activityTracking';
import { reportAdEngagement, serveAd, trackAdClick as trackAdClickServer, trackAdImpression as trackAdImpressionServer } from '../utils/secureFunctions';
import { withClickId } from '../utils/conversions';
import { opensNewTab, runAdAction } from '../utils/adActions';
import { useResolvedMediaUrl } from '../hooks/useResolvedMediaUrl';

interface FeedAdProps {
  placement: 'feed' | 'sidebar' | 'video_midroll';
  contentType?: 'post' | 'pulse';
  contentId?: string;
  onNoAd?: () => void;
  onReady?: () => void;
}

const FeedAd: React.FC<FeedAdProps> = ({ placement, contentType, contentId, onNoAd, onReady }) => {
  const [ad, setAd] = useState<Advertisement | null>(null);
  const [loading, setLoading] = useState(true);

  const handleImpression = useCallback(async (advertisement: Advertisement) => {
    try {
      // Record impression in activity cookie
      trackAdImpression(advertisement.id);

      const res = await trackAdImpressionServer(advertisement.id, { placement, contentType, contentId });
      if (!res.result.success && res.result.adPaused) {
        setAd(null);
      }
    } catch (error) {
      console.error('Failed to track impression:', error);
    }
  }, [placement, contentType, contentId]);

  const loadTargetedAd = useCallback(async () => {
    try {
      // Get user activity profile (behavior tracking)
      const activity = getUserActivity();
      // Get user location (IP-based)
      const userLocation = await getCurrentUserLocation();

      // Ask server for a candidate ad, then apply lightweight client-side targeting.
      // (We removed Firestore user profile reads; advanced targeting can be re-added once profiles migrate.)
      let selectedAd: Advertisement | null = null;
      for (let attempts = 0; attempts < 3; attempts++) {
        const res = await serveAd({ placement, contentType: contentType || null, contentId: contentId || null });
        const candidateAd = (res.ad as Advertisement | null) || null;
        if (!candidateAd) break;

        const ok = (() => {
          if (!shouldShowAd(candidateAd.id)) return false;
          if (candidateAd.budgetSpent >= candidateAd.budget) return false;
          if (candidateAd.targetLocations && userLocation) {
            if (!matchesLocation(userLocation, candidateAd.targetLocations)) return false;
          }
          if (candidateAd.targetInterests && candidateAd.targetInterests.length > 0) {
            const userInterests = activity.interests || {};
            const hasMatchingInterest = candidateAd.targetInterests.some(interest => {
              const behavioralMatch = userInterests[interest] && userInterests[interest] > 2;
              return behavioralMatch;
            });
            if (!hasMatchingInterest) return false;
          }
          if (candidateAd.targetCategories && candidateAd.targetCategories.length > 0) {
            const userInterests = activity.interests || {};
            const hasMatchingInterest = candidateAd.targetCategories.some(cat => userInterests[cat] && userInterests[cat] > 2);
            if (!hasMatchingInterest) return false;
          }
          return true;
        })();

        if (ok) {
          selectedAd = candidateAd;
          break;
        }
      }

      if (!selectedAd) {
        setLoading(false);
        onNoAd?.();
        return;
      }

      setAd(selectedAd);
      onReady?.();
      await handleImpression(selectedAd);
    } catch (error) {
      console.error('Failed to load ad:', error);
      onNoAd?.();
    } finally {
      setLoading(false);
    }
  }, [placement, contentType, contentId, handleImpression, onNoAd, onReady]);

  useEffect(() => {
    loadTargetedAd();
  }, [loadTargetedAd]);

  

  const containerRef = useRef<HTMLDivElement>(null);
  // Ad media is stored as private object keys; resolve them to short-lived signed URLs.
  const imageSrc = useResolvedMediaUrl(ad?.imageURL || '');
  const videoSrc = useResolvedMediaUrl(ad?.imageURL ? '' : ad?.videoURL || '');
  const adVideoRef = useRef<HTMLVideoElement>(null);

  // Measure how long the ad is actually seen (>= 50% on screen, tab visible). Creators only earn from
  // views of 5s+, and earn more the longer the ad is watched (full share at completion or on click).
  useEffect(() => {
    const el = containerRef.current;
    if (!ad || !el) return;
    let visible = false;
    let watchedMs = 0;
    let reportedMs = 0;
    let completed = false;
    const thresholds = [5000, 10000, 15000, 30000];
    const send = () => {
      if (watchedMs < 5000 || (watchedMs === reportedMs && !completed)) return;
      reportedMs = watchedMs;
      reportAdEngagement(ad.id, { watchedMs: Math.round(watchedMs), completed }).catch(() => undefined);
    };
    const io = new IntersectionObserver(([entry]) => { visible = entry.intersectionRatio >= 0.5; }, { threshold: [0, 0.5, 1] });
    io.observe(el);
    const timer = window.setInterval(() => {
      if (!visible || document.hidden || completed) return;
      const prev = watchedMs;
      watchedMs += 500;
      const v = adVideoRef.current;
      if (v && Number.isFinite(v.duration) && v.duration > 0 && watchedMs >= v.duration * 1000) completed = true;
      if (completed || thresholds.some((t) => prev < t && watchedMs >= t)) send();
    }, 500);
    return () => {
      io.disconnect();
      window.clearInterval(timer);
      send();
    };
  }, [ad]);

  const handleClick = async () => {
    if (!ad) return;
    // Opened synchronously so popup blockers allow it; filled in once the click is recorded.
    const pendingTab = opensNewTab(ad) ? window.open('about:blank', '_blank') : null;
    trackAdClick(ad.id);
    const clickRes = await trackAdClickServer(ad.id, { placement, contentType, contentId }).catch((error) => {
      console.error('Failed to track click:', error);
      return null;
    });
    runAdAction(ad, pendingTab, (url) => withClickId(url, clickRes?.clickId));
  };

  if (loading) {
    return null;
  }

  if (!ad) {
    return null;
  }

  return (
    <div ref={containerRef} style={placement !== 'sidebar' ? styles.feedAdContainer : styles.sidebarAdContainer}>
      <div style={styles.adLabel}>Sponsored</div>
      <div style={styles.adContent} onClick={handleClick}>
        {ad.imageURL ? (
          (imageSrc ? <img src={imageSrc} alt={ad.title} style={styles.adImage} /> : null)
        ) : ad.videoURL ? (
          (videoSrc ? <video ref={adVideoRef} src={videoSrc} style={styles.adImage} autoPlay muted loop playsInline /> : null)
        ) : null}
        <div style={styles.adText}>
          <h3 style={styles.adTitle}>{ad.title}</h3>
          <p style={styles.adDescription}>{ad.description}</p>
          {ad.actionType !== 'none' && ad.buttonText && (
            <button style={styles.adButton}>{ad.buttonText}</button>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  feedAdContainer: {
    width: '100%',
    background: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    border: '1px solid #E8F5F1',
  },
  sidebarAdContainer: {
    width: '100%',
    background: '#fff',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    border: '1px solid #E8F5F1',
  },
  adLabel: {
    fontSize: 11,
    color: '#999',
    marginBottom: 8,
    fontWeight: '500',
  },
  adContent: {
    cursor: 'pointer',
  },
  adImage: {
    width: '100%',
    borderRadius: 8,
    marginBottom: 12,
  },
  adText: {
    padding: '0 4px',
  },
  adTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#072B2C',
    marginBottom: 8,
  },
  adDescription: {
    fontSize: 14,
    color: '#5B6E6E',
    marginBottom: 12,
    lineHeight: 1.4,
  },
  adButton: {
    width: '100%',
    padding: '10px 16px',
    background: '#66EAD1',
    color: '#072B2C',
    border: 'none',
    borderRadius: 6,
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default FeedAd;
